import React, { useState, useEffect, useRef, ChangeEvent } from "react";
import classNames from "classnames";
import styles from "./Field.module.scss";
import Icon from "../images/Icon";

export type MediaType = 'Image' | 'Video' | 'Sound' | 'unknown';

export interface FieldMediaProps {
  placeholder?        : string;
  value?              : string | number;
  mediaURL?           : string;
  readOnly?           : boolean;
  onChange?           : (event: React.ChangeEvent<HTMLInputElement>, value: string | number | File, mediaType?: string, duration?: string) => void;
  /** Appelé quand le fichier choisi ne correspond pas au `forceType` attendu : il est refusé
      avant tout aperçu, et rien n'est remonté par `onChange`. */
  onFileRejected?     : (file: File, expectedType: MediaType) => void;
  onDelete?           : () => void;
  onShowLibrary?      : () => void;
  onView?             : (mediaURL: string, mediaType: MediaType) => void;
  onPreviewClick?     : () => void;
  isLoading?          : boolean;
  loadProgress?       : any;
  width?              : string;
  height?             : string;
  glowColor?          : string;
  iconSquareColor?    : string;
  previewWidth?       : string;
  previewHeight?      : string;
  /** Ratio (ex. "16/9") appliqué au cadre vide quand aucune hauteur fixe n'est
      donnée — sans lui, le cadre vide garde une hauteur fixe (200px) qui
      paraît trop haute une fois la colonne rétrécie. Sans effet dès qu'un
      média est chargé : celui-ci impose alors sa propre proportion. */
  previewAspectRatio? : string;
  previewFit?         : 'cover' | 'contain';
  previewShape?       : 'rectangle' | 'circle';
  previewBordered?    : boolean;
  baseURL?            : string;
  extension?          : string;
  showPreview?        : boolean;
  previewAlwaysShown? : boolean;
  previewPosition?    : 'bottom' | 'left_top' | 'left_center' | 'left_bottom' | 'right_top' | 'right_center' | 'right_bottom';
  previewClickable?   : boolean;
  autoPlay?           : boolean;
  controls?           : boolean;
  muted?              : boolean;
  library?            : boolean;
  upload?             : boolean;
  forceType?          : MediaType;
  externalMode?       : boolean;
  showTextInput?      : boolean;
  showControlsRow?    : boolean;
  fileInputRef?       : React.RefObject<HTMLInputElement>;
  defaultImage?       : string;
  /** Nom du fichier tel qu'il a été téléversé, affiché sur la ligne des icônes. */
  fileName?           : string;
  /** Placeholder illustré (nuage + « Parcourir ») plutôt que la simple phrase. Par défaut : oui. */
  placeholderWithIcon?: boolean;
}

export const getMediaType = (filename: string, forceType?: MediaType): MediaType => {
  if (forceType && forceType !== 'unknown') return forceType;

  const withoutQueryOrHash = filename.split(/[?#]/)[0];
  const ext = withoutQueryOrHash.toLowerCase().split('.').pop() || '';
  
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'ico', 'tiff'];
  if (imageExts.includes(ext)) return 'Image';
  
  const videoExts = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'm3u8', 'flv', 'wmv'];
  if (videoExts.includes(ext)) return 'Video';
  
  const audioExts = ['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a', 'wma', 'opus'];
  if (audioExts.includes(ext)) return 'Sound';
  
  return 'unknown';
};

const extractVideoDuration = (file: File): Promise<string> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      const duration = Math.floor(video.duration);
      const hours = Math.floor(duration / 3600);
      const minutes = Math.floor((duration % 3600) / 60);
      const seconds = duration % 60;
      const formatted = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      resolve(formatted);
    };

    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      resolve('00:00:00');
    };

    video.src = URL.createObjectURL(file);
  });
};

export const FieldMedia = ({ 
  placeholder,
  value = "",
  mediaURL,
  readOnly,
  onChange,
  onFileRejected,
  onDelete,
  onShowLibrary,
  onView,
  onPreviewClick,
  isLoading = false,
  loadProgress = null,
  width = "100%",
  height = "fit-content",
  glowColor = "var(--color-Field-Focus-normal)",
  iconSquareColor = "var(--color-Field-Border-normal)",
  previewWidth,
  previewHeight,
  previewAspectRatio,
  previewFit = 'contain',
  previewShape = 'rectangle',
  previewBordered = false,
  baseURL = "",
  extension = "",
  showPreview = true,
  previewAlwaysShown = false,
  previewPosition = 'bottom',
  previewClickable = true,
  autoPlay = false,
  controls = true,
  muted = true,
  library = false,
  upload = true,
  forceType,
  externalMode = false,
  showTextInput = true,
  showControlsRow = true,
  fileInputRef: externalFileInputRef,
  defaultImage,
  placeholderWithIcon = true,
  fileName,
}: FieldMediaProps) => {
  
  const fieldStyle = {
    "--glow-color": glowColor,
    "--icon-square-color": iconSquareColor,
  } as React.CSSProperties;
  
  const [mediaSrc, setMediaSrc] = useState("");
  const [inputValue, setInputValue] = useState<string | number>(value);
  const [isBlob, setIsBlob] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  const [mediaType, setMediaType] = useState<MediaType>('unknown');

  const internalFileInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = externalFileInputRef || internalFileInputRef;
  const blobUrlRef = useRef<string | null>(null);
  const fileInputId = useRef(`file-input-${Math.random().toString(36).slice(2, 11)}`);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
	  
    const filename = inputValue?.toString() || mediaURL || extension || '';
    const detectedType = getMediaType(filename, forceType);
    setMediaType(detectedType);
  }, [inputValue, mediaURL, extension, forceType]);

  useEffect(() => {

    if (mediaURL && !isBlob) {
      setMediaSrc(mediaURL);
    }
  }, [mediaURL, isBlob]);

  useEffect(() => {
	  
    if (value) {
      setInputValue(value);
      if (typeof value === "string") {
        const fullPath = constructFullPath(value, baseURL, extension);
        setMediaSrc(fullPath);
        setMediaError(false);
        if (isBlob) {
          setIsBlob(false);
          if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
          }
        }
      }
    } else if (!isBlob) {
      setInputValue("");
      setMediaSrc("");
      setMediaError(false);
    }
  }, [value, baseURL, extension]);
  
  const constructFullPath = (value: string, baseURL: string, extension: string): string => {
    if (value.startsWith("blob:") || value.startsWith("http://") || value.startsWith("https://")) {
      return value;
    }
    let fullPath = value;
    if (!value.startsWith("/")) {
      fullPath = `${baseURL}${value}`;
    }
    if (!value.includes(".")) {
      const ext = extension || (mediaType === 'Video' ? '.mp4' : mediaType === 'Sound' ? '.mp3' : '.webp');
      fullPath += ext;
    }
    return fullPath;
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size === 0) {
      console.error("Le fichier est vide");
      return;
    }

    const detectedType = getMediaType(file.name, forceType);
    if (detectedType === 'unknown') {
      console.error("Type de fichier non supporté");
      return;
    }

    // `accept` ne filtre que la boîte de dialogue : « Tous les fichiers » la contourne.
    // Quand un type est imposé, on relit l'extension RÉELLE (sans forceType, qui la
    // court-circuiterait) et on refuse avant de fabriquer le blob — sinon l'aperçu
    // afficherait un fichier que le parent ne recevra jamais.
    if (forceType && forceType !== 'unknown') {
      const realType = getMediaType(file.name);
      if (realType !== forceType) {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        if (onFileRejected) {
          onFileRejected(file, forceType);
        }
        return;
      }
    }

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
    }

    const objectURL = URL.createObjectURL(file);
    blobUrlRef.current = objectURL;

    setInputValue(file.name);
    setMediaSrc(objectURL);
    setIsBlob(true);
    setMediaError(false);

    let duration: string | undefined;
    if (detectedType === 'Video') {
      duration = await extractVideoDuration(file);
    }

    if (onChange) {
      onChange(event, file, detectedType, duration);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setInputValue(input);
    setMediaError(false);

    if (isBlob) {
      setIsBlob(false);
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    }

    if (input) {
      const fullPath = constructFullPath(input, baseURL, extension);
      setMediaSrc(fullPath);
    } else {
      setMediaSrc("");
    }

    if (onChange) {
      onChange(event, input);
    }
  };

  const handleUploadClick = () => {
    if (readOnly) return;
    fileInputRef.current?.click();
  };

  const handleLibraryClick = () => {
    if (readOnly) return;
    if (onShowLibrary) {
      onShowLibrary();
    }
  };

  const handleDeleteClick = () => {
    if (readOnly) return;

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    setMediaSrc("");
    setInputValue("");
    setIsBlob(false);
    setMediaError(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    if (onDelete) {
      onDelete();
    }
  };

  const handleViewClick = () => {
    if (onView) {
      const srcToDisplay = isBlob ? mediaSrc : (mediaURL || mediaSrc);
      onView(srcToDisplay, mediaType);
    }
  };

  const handlePreviewClick = () => {
    if (onPreviewClick) {
      onPreviewClick();
    } else {
      // Comportement par défaut : ouvrir le sélecteur de fichier
      handleUploadClick();
    }
  };

  const handleMediaError = () => {
    setMediaError(true);
  };

  const getAcceptType = (): string => {
    if (forceType) {
      switch (forceType) {
        case 'Image': return 'image/*';
        case 'Video': return 'video/*';
        case 'Sound': return 'audio/*';
        default: break;
      }
    }
    return 'image/*,video/*,audio/*';
  };

  // Carré contenant une icône (upload, library3, trashCan...), même classe que Field.tsx
  const IconSquare = ({
    children,
    onClick,
  }: {
    children: React.ReactNode;
    onClick?: () => void;
  }) => (
    <div className={classNames(styles.icon_square)} onClick={onClick}>
      {children}
    </div>
  );

  const previewShapeStyle: React.CSSProperties = {
    borderRadius: previewShape === 'circle' ? '50%' : '5px',
    border: previewBordered ? '1px solid var(--color-Field-Border-normal)' : 'none',
    ...(previewShape === 'circle' ? { aspectRatio: '1 / 1' } : {}),
  };

  // En cercle, un cadrage "contain" laisserait des bandes visibles dans le rond : on force "cover".
  const effectivePreviewFit = previewShape === 'circle' ? 'cover' : previewFit;

  const renderPlaceholder = (): JSX.Element | null => {
    if (!previewAlwaysShown) return null;

    // Le nuage doit TENIR dans le cadre. Il vaut 82 px partout où la hauteur est
    // libre (l'aperçu d'une fiche vidéo), mais le logo d'une chaîne tient dans
    // 140 px : moins les 40 px de marge, le mot et son écart, il ne reste que
    // 75 px, et `overflow: hidden` couperait le dessin sans prévenir.
    const hauteurCadre = parseInt(String(previewHeight ?? ''), 10);
    const tailleNuage  = Number.isFinite(hauteurCadre) ? Math.max(24, Math.min(82, hauteurCadre - 65)) : 82;
    
    const placeholderStyle = {
      width: previewWidth || "100%",
      height: previewHeight || (previewAspectRatio ? "auto" : "200px"),
      aspectRatio: !previewHeight && previewAspectRatio ? previewAspectRatio : undefined,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--color-Field-BG-normal)',
      color: 'var(--color-Field-FG-normal)',
      fontSize: '14px',
      textAlign: 'center' as const,
      padding: '20px',
      cursor: previewClickable ? 'pointer' : 'default',
      overflow: 'hidden',
      ...previewShapeStyle,
    };

    // Deux mises en scène du vide. L'illustrée dit quoi FAIRE — le cadre entier
    // ouvre déjà le sélecteur (handlePreviewClick), « Parcourir » ne fait que le
    // rendre visible. La phrase seule reste disponible pour les champs en lecture
    // ou trop étroits pour une icône de 48 px.
    return (
      <div 
        style={placeholderStyle}
        onClick={previewClickable ? handlePreviewClick : undefined}
      >
        {
         placeholderWithIcon
         ? <div style={{ display        : 'flex'   ,
                         flexDirection  : 'column' ,
                         alignItems     : 'center' ,
                         justifyContent : 'center' ,
                         gap            : '8px'    }}>
             <Icon name       = "cloud"
                   color      = "var(--color-Field-Icon-normal)"
                   hoverColor = "var(--color-Field-Icon-hover-normal)"
                   width      = {tailleNuage}
                   height     = {tailleNuage} />
             <span>Parcourir</span>
           </div>
         : 'Aucun média sélectionné'
        }
      </div>
    );
  };

  const renderViewer = (): JSX.Element | null => {
	
    const srcToDisplay = isBlob ? mediaSrc : (mediaURL || mediaSrc);
    
    if (!showPreview) return null;
    
    if (isLoading && loadProgress) {
      return (
        <div style={{ padding: '16px', textAlign: 'center', background: '#f0f0f0', borderRadius: '5px' }}>
          Chargement... {loadProgress.percent}%
        </div>
      );
    }
    
    if (!srcToDisplay) {
      if (previewAlwaysShown && defaultImage) {
        const defaultStyle: React.CSSProperties = {
          width: previewWidth || "100%",
          height: previewHeight || "auto",
          maxWidth: "100%",
          position: 'relative',
          overflow: 'hidden',
          cursor: previewClickable ? 'pointer' : 'default',
          ...previewShapeStyle,
        };
        return (
          <div style={defaultStyle} onClick={previewClickable ? handlePreviewClick : undefined}>
            <img
              src={defaultImage}
              alt="Image par défaut"
              style={{ width: '100%', height: '100%', objectFit: effectivePreviewFit, display: 'block' }}
            />
          </div>
        );
      }
      return previewAlwaysShown ? renderPlaceholder() : null;
    }
    
    if (mediaError) {
      return (
        <div style={{ padding: '16px', color: '#d32f2f', background: '#ffebee', borderRadius: '5px' }}>
          Impossible de charger le média
        </div>
      );
    }

    const previewStyle = {
      width: previewWidth || "100%",
      height: previewHeight || "auto",
      maxWidth: "100%",
      position: 'relative' as const,
      overflow: 'hidden' as const,
      backgroundColor: 'var(--color-Field-BG-normal)',
      cursor: previewClickable ? 'pointer' : 'default',
      ...previewShapeStyle,
    };

    switch (mediaType) {
      case 'Image':
        return (
          <div style={previewStyle} onClick={previewClickable ? handlePreviewClick : undefined}>
            <img
              src={srcToDisplay}
              alt="Preview"
              style={{ width: '100%', height: '100%', objectFit: effectivePreviewFit, display: 'block' }}
              onError={handleMediaError}
            />
            {onView && (
              <button
                onClick={handleViewClick}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  padding: '4px 8px',
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Icon name       = "eye" 
				      color      = "var(--color-Field-Icon-normal)"
                      hoverColor = "var(--color-Field-Icon-hover-normal)" 
					  width      = {16} 
					  height     = {16} />
                Zoom
              </button>
            )}
          </div>
        );

      case 'Video':
        return (
          <div style={previewStyle} onClick={previewClickable ? handlePreviewClick : undefined}>
            <video
              controls={controls}
              autoPlay={autoPlay}
              muted={muted}
              playsInline
              onError={handleMediaError}
              style={{ width: "100%", height: "100%", objectFit: effectivePreviewFit, display: "block" }}
            >
              <source src={srcToDisplay} />
              Votre navigateur ne supporte pas la lecture de vidéos.
            </video>
            {onView && (
              <button
                onClick={handleViewClick}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  padding: '4px 8px',
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Icon name       = "eye" 
				      color      = "var(--color-Field-Icon-normal)"
                      hoverColor = "var(--color-Field-Icon-hover-normal)" 
					  width      = {16} 
					  height     = {16} />
                Zoom
              </button>
            )}
          </div>
        );

      case 'Sound':
        return (
          <div style={previewStyle} onClick={previewClickable ? handlePreviewClick : undefined}>
            <audio
              src={srcToDisplay}
              controls
              autoPlay={autoPlay}
              onError={handleMediaError}
              style={{ width: "100%" }}
            />
            {onView && (
              <button
                onClick={handleViewClick}
                style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  background: glowColor,
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  width: '100%',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <Icon name       = "eye" 
				      color      = "var(--color-Field-Icon-normal)"
                      hoverColor = "var(--color-Field-Icon-hover-normal)" 
					  width      = {16} 
					  height     = {16} />
                Ouvrir le lecteur
              </button>
            )}
          </div>
        );

      default:
        return previewAlwaysShown ? renderPlaceholder() : null;
    }
  };

  const renderInputSection = () => (
    <>
      <input
        ref={fileInputRef}
        type="file"
        id={fileInputId.current}
        style={{ display: 'none' }}
        accept={getAcceptType()}
        onChange={handleFileChange}
        disabled={readOnly}
      />
      
      {!externalMode && showControlsRow && (
        <div className={classNames(styles.field_with_icon)}>
          {showTextInput && (
            <input
              type="text"
              className={classNames(styles.field_input)}
              placeholder={placeholder}
              value={typeof inputValue === "string" ? inputValue : ""}
              readOnly={readOnly}
              onChange={handleTextChange}
            />
          )}
          {!readOnly && upload && (
            <IconSquare onClick={handleUploadClick}>
              <Icon
                name       = "upload"
                color      = "var(--color-Field-Icon-normal)"
                hoverColor = "var(--color-Field-Icon-hover-normal)"
                width      = {18}
                height     = {18}
              />
            </IconSquare>
          )}
          {!readOnly && library && (
            <IconSquare onClick={handleLibraryClick}>
              <Icon
                name       = "library3"
                color      = "var(--color-Field-Icon-normal)"
                hoverColor = "var(--color-Field-Icon-hover-normal)"
                width      = {18}
                height     = {18}
              />
            </IconSquare>
          )}
          {!readOnly && onDelete && (
            <IconSquare onClick={handleDeleteClick}>
              <Icon
                name       = "trashCan"
                color      = "var(--color-Field-Icon-normal)"
                hoverColor = "var(--color-Field-Icon-hover-normal)"
                width      = {18}
                height     = {18}
              />
            </IconSquare>
          )}
        </div>
      )}
    </>
  );

  const renderContent = () => {
    const isLeftPosition = previewPosition?.startsWith('left');
    const isRightPosition = previewPosition?.startsWith('right');
    
    if (isLeftPosition || isRightPosition) {
      // Déterminer l'alignement vertical
      let alignItems: 'flex-start' | 'center' | 'flex-end' = 'flex-start';
      if (previewPosition?.endsWith('_center')) {
        alignItems = 'center';
      } else if (previewPosition?.endsWith('_bottom')) {
        alignItems = 'flex-end';
      } else if (previewPosition?.endsWith('_top')) {
        alignItems = 'flex-start';
      }

      const containerStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: isLeftPosition ? 'row' : 'row-reverse',
        gap: '16px',
        alignItems: alignItems,
        width: '100%'
      };

      const previewContainerStyle: React.CSSProperties = {
        flex: previewWidth ? `0 0 ${previewWidth}` : '0 0 300px',
        minWidth: 0
      };

      const inputContainerStyle: React.CSSProperties = {
        flex: '1',
        minWidth: 0
      };

      return (
        <div style={containerStyle}>
          {/* Le nom coiffe l'APERÇU, pas la rangée d'icônes : il se retrouve ainsi à la
              même hauteur qu'elles — elles occupent le haut de la colonne voisine — et
              l'aperçu descend d'autant. */}
          <div style={previewContainerStyle}>
            {fileName && (
              <div className={classNames(styles.file_name)} title={fileName}>{fileName}</div>
            )}
            {renderViewer()}
          </div>
          <div style={inputContainerStyle}>
            {renderInputSection()}
          </div>
        </div>
      );
    } else {
      // Position bottom (par défaut)
      return (
        <>
          {renderInputSection()}
          {fileName && (
            <div className={classNames(styles.file_name)} title={fileName}>{fileName}</div>
          )}
          {renderViewer()}
        </>
      );
    }
  };

  return (
    <div className={classNames(styles.field)} style={{ width, height, ...fieldStyle }}>
      <div className={classNames(styles.file_input_wrapper)}>
        {renderContent()}
      </div>
    </div>
  );
};

export default FieldMedia;